import { ClerkProvider } from '@clerk/clerk-react';
import { RouterProvider } from '@tanstack/react-router';
import { AbacProvider, rules } from '@uturn/portal/abac';
import { useAuth } from '@uturn/portal/modules/auth/hooks';
import { AuthProvider } from '@uturn/portal/modules/auth/provider';
import { queryClient } from '@uturn/portal/query';
import { ThemeProvider } from '@uturn/ui-kit';
import type { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
	ClerkErrorBoundary,
	EnvBanner,
	GenericErrorBoundary,
	LoadingSpinnerWithLogo,
} from './components';
import { HttpClientProvider } from './http';
import { Providers } from './providers';
import { router } from './router/router';
import { Scripts } from './scripts';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!CLERK_PUBLISHABLE_KEY) {
	throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
}

// Pin the Clerk JS (clerk.browser.js) version so the browser requests the exact
// versioned URL and skips Clerk's version-resolution redirect hop. This reduces
// `failed_to_load_clerk_js_timeout` failures. Keep in sync with the
// @clerk/clerk-react major when upgrading. See https://clerk.com/docs/pinning
const CLERK_JS_VERSION = '5.127.1';

const options: Partial<PostHogConfig> = {
	api_host: import.meta.env.VITE_POSTHOG_HOST,
	disable_session_recording: false,
	capture_pageleave: true,
	capture_pageview: true,
	autocapture: true,
	session_recording: {
		maskAllInputs: false,
		maskInputOptions: {
			password: true,
		},
		maskInputFn: (text: string, element?: HTMLElement) => {
			if (element?.classList.contains('password')) {
				return '*'.repeat(text.length);
			}
			return text;
		},
	},
};

const InnerApp = () => {
	const auth = useAuth();

	if (!auth.isLoaded) {
		return <LoadingSpinnerWithLogo />;
	}

	return <RouterProvider router={router} context={{ auth, queryClient }} />;
};

const App = () => {
	const { t } = useTranslation();

	return (
		<PostHogProvider
			apiKey={import.meta.env.VITE_POSTHOG_KEY}
			options={options}
		>
			<HelmetProvider>
				<Helmet
					title={t('general.title', 'Portal').toString()}
					titleTemplate={'%s | UTURN'}
				/>
				<Scripts />
				<ThemeProvider
					defaultMode="light"
					defaultColor="uturn"
					storageKey="platform-theme"
				>
					<EnvBanner />
					{/* Outer boundary: catches everything ClerkErrorBoundary re-throws. */}
					<GenericErrorBoundary>
						<ClerkErrorBoundary>
							<ClerkProvider
								publishableKey={CLERK_PUBLISHABLE_KEY}
								clerkJSVersion={CLERK_JS_VERSION}
								afterSignOutUrl="/"
							>
								<HttpClientProvider>
									<Providers>
										<AuthProvider>
											<AbacProvider rules={rules}>
												<InnerApp />
											</AbacProvider>
										</AuthProvider>
									</Providers>
								</HttpClientProvider>
							</ClerkProvider>
						</ClerkErrorBoundary>
					</GenericErrorBoundary>
				</ThemeProvider>
			</HelmetProvider>
		</PostHogProvider>
	);
};

export default App;
